import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { adminAPI } from '../utils/api';

const AdminPanel = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editClient, setEditClient] = useState(null);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminAPI.listClients({ search: search || undefined });
      setClients(res.data.clients || res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [search]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete shop "${name}"? This cannot be undone.`)) return;
    try { await adminAPI.deleteClient(id); load(); } catch (e) { alert('Failed to delete'); }
  };

  const plans = { free: 'bg-gray-100 text-gray-800', basic: 'bg-blue-100 text-blue-800', premium: 'bg-purple-100 text-purple-800' };
  const statuses = { active: 'bg-green-100 text-green-800', suspended: 'bg-yellow-100 text-yellow-800', cancelled: 'bg-red-100 text-red-800' };

  return (
    <Layout>
      <div className="page-enter max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Shop Owners</h1>
            <p className="text-gray-600 mt-1">Manage client accounts and subscriptions</p>
          </div>
          <button onClick={() => { setEditClient(null); setShowModal(true); }}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            ➕ Add Shop Owner
          </button>
        </div>

        {/* Stats */}
        <div className="stagger-list grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-2xl font-bold text-gray-900">{clients.length}</p>
            <p className="text-xs text-gray-500">Total Clients</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-2xl font-bold text-green-700">{clients.filter(c => c.status === 'active').length}</p>
            <p className="text-xs text-gray-500">Active</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-2xl font-bold text-purple-700">{clients.filter(c => c.plan === 'premium').length}</p>
            <p className="text-xs text-gray-500">Premium Plan</p>
          </div>
        </div>

        {/* Search */}
        <div className="bg-white rounded-lg shadow p-4 mb-4">
          <input type="text" placeholder="Search by shop name, owner or email..."
            value={search} onChange={e => setSearch(e.target.value)}
            className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" />
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow overflow-x-auto">
          {loading ? (
            <div className="text-center py-16 text-gray-500">Loading clients...</div>
          ) : clients.length === 0 ? (
            <div className="text-center py-16">
              <div className="text-6xl mb-4">🏪</div>
              <p className="text-gray-600 mb-4">No shop owners registered yet</p>
              <button onClick={() => setShowModal(true)} className="px-4 py-2 bg-blue-600 text-white rounded-lg">Add First Client</button>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  {['Shop Name', 'Owner', 'Email', 'Plan', 'Status', 'Actions'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {clients.map(c => (
                  <tr key={c.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-900">{c.shop_name || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{c.owner_name || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{c.email}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${plans[c.plan] || 'bg-gray-100 text-gray-800'}`}>{c.plan || 'free'}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${statuses[c.status] || 'bg-gray-100 text-gray-800'}`}>{c.status || 'active'}</span>
                    </td>
                    <td className="px-4 py-3 text-sm whitespace-nowrap">
                      <button onClick={() => { setEditClient(c); setShowModal(true); }} className="text-blue-600 hover:underline mr-3">Edit</button>
                      <button onClick={() => handleDelete(c.id, c.shop_name || c.email)} className="text-red-600 hover:underline">Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showModal && (
        <ClientModal client={editClient}
          onClose={() => { setShowModal(false); setEditClient(null); }}
          onSuccess={() => { setShowModal(false); setEditClient(null); load(); }} />
      )}
    </Layout>
  );
};

const ClientModal = ({ client, onClose, onSuccess }) => {
  const [form, setForm] = useState({
    email: client?.email || '',
    password: '',
    shop_name: client?.shop_name || '',
    owner_name: client?.owner_name || '',
    plan: client?.plan || 'free',
    status: client?.status || 'active',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [dirty, setDirty] = useState(false);

  const set = (k, v) => { setDirty(true); setForm(f => ({ ...f, [k]: v })); };

  const handleClose = () => {
    if (dirty && !window.confirm('Discard changes? All unsaved changes will be lost.')) return;
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      const data = { ...form };
      if (!data.password && !client) { setError('Password is required'); setSaving(false); return; }
      if (!data.password) delete data.password;
      if (client) await adminAPI.updateClient(client.id, data);
      else await adminAPI.createClient(data);
      onSuccess();
    } catch (e) { setError(e.response?.data?.detail || 'Failed to save'); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="animate-scaleIn bg-white rounded-lg p-6 w-full" style={{ maxWidth: 'min(1120px, calc(100vw - 12px))', maxHeight: '98dvh', overflowY: 'auto' }}>
        <h2 className="text-xl font-bold mb-4">{client ? 'Edit Shop Owner' : 'Add New Shop Owner'}</h2>
        {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded text-sm">{error}</div>}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
            <input required type="email" value={form.email} onChange={e => set('email', e.target.value)} disabled={!!client}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{client ? 'New Password (leave blank to keep)' : 'Password *'}</label>
            <input type="password" value={form.password} onChange={e => set('password', e.target.value)}
              placeholder={client ? '••••••••' : 'Min 8 characters'}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Shop Name</label>
              <input value={form.shop_name} onChange={e => set('shop_name', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Owner Name</label>
              <input value={form.owner_name} onChange={e => set('owner_name', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Plan</label>
              <select value={form.plan} onChange={e => set('plan', e.target.value)} className="w-full px-3 py-2 border rounded-lg">
                <option value="free">Free</option>
                <option value="basic">Basic</option>
                <option value="premium">Premium</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select value={form.status} onChange={e => set('status', e.target.value)} className="w-full px-3 py-2 border rounded-lg">
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={handleClose} className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-100">Cancel</button>
            <button type="submit" disabled={saving} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-300">
              {saving ? 'Saving...' : client ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminPanel;
